import type { PropsWithChildren } from "react";

// eslint-disable-next-line no-restricted-imports -- RVSidedrawer is the wrapper component that needs to import Drawer from antd
import { Drawer } from "antd";
import { clsx } from "clsx";

import type { DrawerProps } from "antd";

import styles from "./RVSideDrawer.module.scss";

/**
 * The subset of `DrawerProps` that `RVSideDrawer` owns as explicit defaults.
 *
 * These keys are intentionally excluded from the public prop surface and must
 * be customised through the `overrides` prop so there is a single, auditable
 * place for all deviation from Regal's drawer baseline.
 */
type RVSideDrawerDefaultAttributes = Pick<
    DrawerProps,
    "closable" | "destroyOnClose" | "maskClosable" | "title" | "width"
>;

/**
 * The narrow set of `DrawerProps` that callers may supply directly.
 *
 * `footer` and `open` are forwarded verbatim. `getContainer` is the opt-in
 * escape hatch that lets a caller dock the drawer inside a specific element
 * (or `false` to render in place) instead of `document.body` — used for in-page
 * overlay panels scoped to a region. `mask` lets a caller hide the dimmed
 * backdrop (e.g. for in-page panels). All other antd props stay managed via
 * `RVSideDrawerDefaultAttributes` / `overrides` to keep the surface predictable.
 */
type RVSideDrawerPickedProps = Pick<DrawerProps, "footer" | "open" | "getContainer" | "mask">;

/**
 * Overrides for wrapper-managed defaults.
 *
 * Limited to the keys in `RVSideDrawerDefaultAttributes` so the `overrides`
 * prop cannot become an unrestricted second bag of `DrawerProps`.
 */
type RVSideDrawerOverrides = Partial<RVSideDrawerDefaultAttributes>;

const DEFAULT_ATTRIBUTES: RVSideDrawerDefaultAttributes = {
    closable: true,
    destroyOnClose: true,
    maskClosable: true,
    title: " ",
    width: "45%",
};

export type RVSideDrawerProps = PropsWithChildren<{
    /** Invoked when the user dismisses the drawer. */
    onClose: () => void;
    /** Additional CSS class merged with the base `rvSideDrawer` class. */
    className?: string;
    /** Hides the drawer header section entirely. */
    hideHeader?: boolean;
    /**
     * Overrides for wrapper-managed defaults only:
     * `closable`, `destroyOnClose`, `maskClosable`, `title`, and `width`.
     *
     * Other `DrawerProps` are not accepted here — the public prop surface is
     * intentionally limited to `footer`, `open`, `onClose`, `className`, and
     * `overrides`.
     */
    overrides?: RVSideDrawerOverrides;
}> &
    RVSideDrawerPickedProps;

/**
 * Regal wrapper around antd `Drawer` with standardized side-drawer defaults.
 *
 * **Public prop surface** (intentionally narrow):
 * - `open` / `onClose` / `footer` — forwarded verbatim to `Drawer`.
 * - `className` — merged with the base `rvSideDrawer` class via `clsx`.
 * - `hideHeader` — removes the header section when true.
 * - `overrides` — tunes wrapper-managed defaults (`closable`, `destroyOnClose`,
 *   `maskClosable`, `title`, `width`).
 *
 * **Default attribute precedence:** `DEFAULT_ATTRIBUTES` → `overrides`.
 *
 * @example
 * // Basic usage
 * <RVSideDrawer open={isOpen} onClose={() => setIsOpen(false)}>
 *   Drawer content
 * </RVSideDrawer>
 *
 * @example
 * // Customise wrapper-managed defaults
 * <RVSideDrawer
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   overrides={{ width: "60%", title: "Edit Settings" }}
 * >
 *   Drawer content
 * </RVSideDrawer>
 *
 * @example
 * // Pass a footer and an extra class
 * <RVSideDrawer
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   footer={<RVButton>Save</RVButton>}
 *   className="myDrawer"
 * >
 *   Drawer content
 * </RVSideDrawer>
 */
export const RVSideDrawer = ({
    children,
    className,
    onClose,
    overrides = {},
    open,
    footer,
    hideHeader = false,
    getContainer,
    mask,
}: RVSideDrawerProps) => {
    const resolvedOverrides: RVSideDrawerOverrides = { ...overrides };

    if (hideHeader) {
        resolvedOverrides.closable = false;
        resolvedOverrides.title = null;
    }

    return (
        <Drawer
            className={clsx(styles.rvSideDrawer, { [styles.hideHeader]: hideHeader }, className)}
            onClose={onClose}
            open={open}
            footer={footer}
            {...(getContainer !== undefined ? { getContainer } : {})}
            {...(mask !== undefined ? { mask } : {})}
            {...DEFAULT_ATTRIBUTES}
            {...resolvedOverrides}
        >
            {children}
        </Drawer>
    );
};
